import React from 'react';
import { useToast } from '../../hooks/useToast';

const ToastIcon = ({ type }) => {
  switch (type) {
    case 'success':
      return (
        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    case 'award':
      return (
        <span className="text-2xl">🏆</span>
      );
    default:
      return (
        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      );
  }
};

const toastStyles = {
  success: {
    container: 'bg-green-100 border-green-400 shadow-green-200/50',
    accent: 'bg-green-500',
  },
  error: {
    container: 'bg-red-100 border-red-400 shadow-red-200/50',
    accent: 'bg-red-500',
  },
  info: {
    container: 'bg-blue-100 border-blue-400 shadow-blue-200/50',
    accent: 'bg-blue-500',
  },
  award: {
    container: 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-400 shadow-yellow-300/50',
    accent: 'bg-gradient-to-b from-yellow-400 to-amber-500',
  },
};

const ToastItem = ({ toast, onClose }) => {
  const styles = toastStyles[toast.type] || toastStyles.info;

  return (
    <div
      className={`
        relative overflow-hidden flex items-start gap-4 pl-6 pr-5 py-4 rounded-xl border-2
        shadow-xl animate-toast-enter
        ${styles.container}
      `}
      role="alert"
    >
      {/* Colored accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${styles.accent}`} />

      <div className="flex-shrink-0 mt-0.5">
        <ToastIcon type={toast.type} />
      </div>
      <p className="text-base font-semibold text-gray-800 flex-1 leading-relaxed">{toast.message}</p>
      <button
        onClick={() => onClose(toast.id)}
        className="flex-shrink-0 text-gray-500 hover:text-gray-700 transition-colors mt-0.5"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col-reverse gap-3 w-80 sm:w-96">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  );
};
